/**
 * Connected Task Operations
 * Creates tasks connected to existing tasks and inserts nodes on edges
 */
import { getDependencyTaskId } from '../utils';
import { hasAnyConditions, replaceTaskInConditions } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { applyConnectedTaskMilestonePlacement } from '../../../FlowchartView/utils/connectedMilestonePlacement';
import { defaultWaitTitle } from '../../../../../utils/waitFor';

function connectedTypeName(nodeType) {
  if (nodeType === 'decision') return 'Decision';
  if (nodeType === 'wait') return 'Wait for';
  return 'Mission';
}

function withWaitDefaults(task, nodeType) {
  if (nodeType !== 'wait') return task;
  return {
    ...task,
    name: defaultWaitTitle('email'),
    nodeType: 'wait',
    waitFor: 'email',
    waitDetails: '',
    fulfilledAt: null,
  };
}

/**
 * Create a new task connected to an existing task
 */
export const createConnectedTaskHandler = (localSprint, setLocalSprint, recordAction) => {
  return (taskId, sourceTaskId, nodeType, position, branch, options = {}) => {
    const now = new Date().toISOString();
    // Find the source task to inherit its sourceCampaign (for multi-campaign mode)
    const sourceTask = localSprint?.tasks?.find(t => t.id === sourceTaskId);

    let dependsOn = branch
      ? [{ taskId: sourceTaskId, branch }]
      : [sourceTaskId];
    let unlockConditions;

    if (options.insertBeforeHost) {
      if (hasAnyConditions(sourceTask?.unlockConditions)) {
        dependsOn = [];
        unlockConditions = sourceTask.unlockConditions;
      } else {
        dependsOn = Array.isArray(sourceTask?.dependsOn) ? [...sourceTask.dependsOn] : [];
      }
    }
    
    const newTask = {
      id: taskId,
      name: '',
      order: (localSprint?.tasks?.length || 0) + 1,
      groupId: null,
      dependsOn,
      status: 'pending',
      x: position?.x,
      y: position?.y,
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      completedAt: null,
      estimatedDurationMinutes: null,
      dueDate: null,
      ...(unlockConditions && { unlockConditions }),
      ...(nodeType !== 'task' && { nodeType }),
      ...(sourceTask?.sourceCampaign && { sourceCampaign: sourceTask.sourceCampaign }),
    };
    Object.assign(newTask, withWaitDefaults(newTask, nodeType));

    const previousTasks = localSprint?.tasks || [];
    const withNew = [...previousTasks, newTask];
    const nextTasks = options.placement && options.feed
      ? applyConnectedTaskMilestonePlacement(withNew, {
          newTaskId: taskId,
          sourceTaskId,
          branch,
          placement: options.placement,
          feed: options.feed,
          afterMilestoneId: options.afterMilestoneId || null,
        })
      : withNew;

    setLocalSprint(prev => {
      if (!prev) return prev;
      return { ...prev, tasks: nextTasks };
    });

    // Record for undo
    if (recordAction) {
      const typeName = connectedTypeName(nodeType);
      const placementLabel = options.placement === 'before'
        ? ' before milestone'
        : options.placement === 'after'
          ? ' after milestone'
          : '';
      recordAction({
        type: 'ADD_CONNECTED_TASK',
        description: `Add connected ${typeName}${placementLabel}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: previousTasks };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: nextTasks };
          });
        },
      });
    }
  };
};

/**
 * Helper to check if task uses unlockConditions as source of truth
 */
const hasUnlockConditions = (task) => {
  return hasAnyConditions(task?.unlockConditions);
};

/**
 * Insert a node on an edge (between two connected nodes)
 * Creates a new node and rewires the dependency: fromTaskId -> newNode -> toTaskId
 * 
 * Handles both legacy dependsOn and new unlockConditions systems:
 * - If target task uses unlockConditions, update those
 * - Otherwise, update dependsOn
 * 
 * NOTE: This handler does NOT record the undo action immediately.
 * Instead, it returns the data needed for undo, and the caller is responsible
 * for recording the action AFTER the tidy operation completes (so positions can be included).
 */
export const createInsertNodeOnEdgeHandler = (localSprint, setLocalSprint, recordAction) => {
  return (taskId, fromTaskId, toTaskId, branch, nodeType, position, options = {}) => {
    const now = new Date().toISOString();
    // Find the source task to inherit its sourceCampaign
    const sourceTask = localSprint?.tasks?.find(t => t.id === fromTaskId);
    
    // Create the new node that depends on the source node
    const newTask = {
      id: taskId,
      name: '',
      order: (localSprint?.tasks?.length || 0) + 1,
      groupId: null,
      dependsOn: branch 
        ? [{ taskId: fromTaskId, branch }]
        : [fromTaskId],
      status: 'pending',
      x: position?.x,
      y: position?.y,
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      completedAt: null,
      estimatedDurationMinutes: null,
      dueDate: null,
      ...(nodeType !== 'task' && { nodeType }),
      ...(sourceTask?.sourceCampaign && { sourceCampaign: sourceTask.sourceCampaign }),
    };
    Object.assign(newTask, withWaitDefaults(newTask, nodeType));

    const targetTask = localSprint?.tasks?.find(t => t.id === toTaskId);
    const originalDependsOn = targetTask?.dependsOn ? [...targetTask.dependsOn] : [];
    const originalUnlockConditions = targetTask?.unlockConditions;
    const targetUsesConditions = hasUnlockConditions(targetTask);

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      
      const updatedTasks = tasks.map(t => {
        if (t.id !== toTaskId) return t;
        
        if (hasUnlockConditions(t)) {
          const newConditions = replaceTaskInConditions(t.unlockConditions, fromTaskId, taskId, branch);
          return { ...t, unlockConditions: newConditions };
        } else {
          const newDependsOn = (Array.isArray(t.dependsOn) ? t.dependsOn : []).map(dep => {
            const depId = getDependencyTaskId(dep);
            if (depId === fromTaskId) {
              if (typeof dep === 'object' && dep.branch) {
                return { ...dep, taskId: taskId };
              }
              return taskId;
            }
            return dep;
          });
          return { ...t, dependsOn: newDependsOn };
        }
      });
      
      return { ...prev, tasks: [...updatedTasks, newTask] };
    });

    // If skipRecordAction is true, don't record immediately - return a function to record later
    // This is used when the caller wants to record a combined action (insert + tidy positions)
    if (options.skipRecordAction && recordAction) {
      const typeName = connectedTypeName(nodeType);
      
      // Return a function that records the combined action with position restoration
      return {
        recordCombinedAction: (originalPositions) => {
          recordAction({
            type: 'INSERT_NODE_ON_EDGE',
            description: `Insert ${typeName} on connection`,
            undo: () => {
              setLocalSprint(prev => {
                if (!prev) return prev;
                const tasks = prev.tasks
                  .filter(t => t.id !== taskId)
                  .map(t => {
                    let updatedTask = t;
                    
                    // Restore dependency for target task
                    if (t.id === toTaskId) {
                      updatedTask = { 
                        ...updatedTask, 
                        dependsOn: originalDependsOn,
                        unlockConditions: originalUnlockConditions || t.unlockConditions,
                      };
                    }
                    
                    // Restore position if we have it (applies to ALL tasks including toTaskId)
                    const origPos = originalPositions?.[t.id];
                    if (origPos) {
                      updatedTask = { ...updatedTask, x: origPos.x, y: origPos.y };
                    }
                    
                    return updatedTask;
                  });
                return { ...prev, tasks };
              });
            },
            redo: () => {
              setLocalSprint(prev => {
                if (!prev) return prev;
                const tasks = prev.tasks.map(t => {
                  if (t.id === toTaskId) {
                    if (targetUsesConditions) {
                      const newConditions = replaceTaskInConditions(t.unlockConditions, fromTaskId, taskId, branch);
                      return { ...t, unlockConditions: newConditions };
                    } else {
                      const newDependsOn = (Array.isArray(t.dependsOn) ? t.dependsOn : []).map(dep => {
                        const depId = getDependencyTaskId(dep);
                        if (depId === fromTaskId) {
                          if (typeof dep === 'object' && dep.branch) {
                            return { ...dep, taskId: taskId };
                          }
                          return taskId;
                        }
                        return dep;
                      });
                      return { ...t, dependsOn: newDependsOn };
                    }
                  }
                  return t;
                });
                return { ...prev, tasks: [...tasks, newTask] };
              });
            },
          });
        },
      };
    }

    // Record for undo (legacy behavior - when not using combined action)
    if (recordAction) {
      const typeName = connectedTypeName(nodeType);
      recordAction({
        type: 'INSERT_NODE_ON_EDGE',
        description: `Insert ${typeName} on connection`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            const tasks = prev.tasks
              .filter(t => t.id !== taskId)
              .map(t => {
                if (t.id === toTaskId) {
                  return { 
                    ...t, 
                    dependsOn: originalDependsOn,
                    unlockConditions: originalUnlockConditions || t.unlockConditions,
                  };
                }
                return t;
              });
            return { ...prev, tasks };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            const tasks = prev.tasks.map(t => {
              if (t.id === toTaskId) {
                if (targetUsesConditions) {
                  const newConditions = replaceTaskInConditions(t.unlockConditions, fromTaskId, taskId, branch);
                  return { ...t, unlockConditions: newConditions };
                } else {
                  const newDependsOn = (Array.isArray(t.dependsOn) ? t.dependsOn : []).map(dep => {
                    const depId = getDependencyTaskId(dep);
                    if (depId === fromTaskId) {
                      if (typeof dep === 'object' && dep.branch) {
                        return { ...dep, taskId: taskId };
                      }
                      return taskId;
                    }
                    return dep;
                  });
                  return { ...t, dependsOn: newDependsOn };
                }
              }
              return t;
            });
            return { ...prev, tasks: [...tasks, newTask] };
          });
        },
      });
    }
    
    return null;
  };
};
